import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import cn from 'classnames';
import { FaRegTrashAlt } from 'react-icons/fa';
import { IoIosArrowDown, IoIosArrowBack } from 'react-icons/io';
import 'moment/locale/ru';
import 'react-datepicker/dist/react-datepicker.css';

import CustomSelect from '../CustomSelect';
import usePageWidth from '../shared/usePageWidth';
import * as facultyActions from '../../actions/faculty';
import * as personnelActions from '../../actions/personnel';
import { createNotification } from '../notifications/notifications';
import styles from './styles.module.css';

/* Режим «Расход личного состава»: состав учебных групп на дату и отметки
   отсутствия. Просмотровый — единственное изменяющее действие — отметка
   отсутствия обучающегося и её снятие. Эталон — макет personnel_mockup.html. */

// «Все» в CustomSelect — пустая строка; в запрос такое значение не уходит
const ALL = '';
const DATE_FORMAT = 'DD.MM.YYYY';
const SAVED_FLASH_MS = 1200;

const today = () => moment().startOf('day');

// отметка учитывается, только если сервер вернул её значение
const absenceOf = student => (student.absence && student.absence.valid ? student.absence : null);

// одноимённые группы разных факультетов в перечне неразличимы — добавляем факультет и курс
const groupMeta = group => [
  group.faculty,
  group.course || group.course === 0 ? `${group.course} курс` : null,
].filter(Boolean);

const Personnel = () => {
  usePageWidth('pageWide');

  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  // переход из журнала учёта учебных занятий: дата занятия и возврат назад
  const fromJournal = location.state?.forPersonnel;

  const facultyList = useSelector(state => state.faculty.list);
  const courses = useSelector(state => state.personnel.courses);
  const groups = useSelector(state => state.personnel.groupsBySchedule);
  const personnel = useSelector(state => state.personnel.personnel);
  const loading = useSelector(state => state.personnel.isLoading);
  const loaded = useSelector(state => state.personnel.personnelLoaded);
  const canEditAbsence = useSelector(state => state.personnel.canEditAbsence);
  const absentGrades = useSelector(state => state.personnel.absentGrades);
  const isTeacher = useSelector(state => state.auth.isTeacher);

  // право изменять отметки приходит с расходом; пока сервер его не отдаёт —
  // действует то же условие, что и на сохранении отметки (постоянный состав)
  const canEdit = canEditAbsence == null ? !!isTeacher : !!canEditAbsence;

  const [date, setDate] = useState(() => (
    fromJournal?.lessonfulldate ? moment(fromJournal.lessonfulldate).startOf('day') : today()
  ));
  const [facultyId, setFacultyId] = useState(ALL);
  const [eyid, setEyid] = useState(ALL);
  const [gid, setGid] = useState(ALL);

  // открытый перечень причин: { gid, mid, anchor }
  const [menu, setMenu] = useState(null);
  const [menuPos, setMenuPos] = useState(null);
  // ячейка, отметку в которой только что сохранили
  const [saved, setSaved] = useState(null);

  const menuRef = useRef(null);
  const savedTimer = useRef(null);

  const dateKey = date ? date.format('YYYY-MM-DD') : '';

  /* ------------------------------- перечни ------------------------------- */
  const facultyOptions = useMemo(
    () => (facultyList || []).map(item => ({ value: String(item.idfaculty), name: item.faculty })),
    [facultyList]
  );

  const courseOptions = useMemo(
    () => (courses || []).map(item => ({ value: String(item.eyid), name: item.name })),
    [courses]
  );

  const groupOptions = useMemo(
    () => (groups || []).map(item => {
      const meta = groupMeta(item).join(', ');
      return { value: String(item.gid), name: meta ? `${item.name} · ${meta}` : item.name };
    }),
    [groups]
  );

  /* ------------------------------- загрузка ------------------------------- */
  useEffect(() => {
    dispatch(facultyActions.list());
    dispatch(personnelActions.fetchAbsentGrades());
    return () => {
      dispatch(personnelActions.rashodRemove());
      if (savedTimer.current) clearTimeout(savedTimer.current);
    };
  }, [dispatch]);

  // перечень курсов зависит от даты и факультета; в зависимостях — dateKey,
  // а не сама дата: moment при каждом выборе новый и эффект бил бы вхолостую
  useEffect(() => {
    if (dateKey) dispatch(personnelActions.fetchRashodCourses(moment(dateKey), facultyId));
  }, [dateKey, facultyId, dispatch]);

  // перечень групп — ещё и от курса
  useEffect(() => {
    if (dateKey) dispatch(personnelActions.fetchGroupsByScheduleDate(moment(dateKey), facultyId, eyid));
  }, [dateKey, facultyId, eyid, dispatch]);

  // ранее выбранное значение, не подходящее под новые условия, возвращается к «Все»
  useEffect(() => {
    if (eyid !== ALL && !(courses || []).some(item => String(item.eyid) === eyid)) setEyid(ALL);
  }, [courses, eyid]);

  useEffect(() => {
    if (gid !== ALL && !(groups || []).some(item => String(item.gid) === gid)) setGid(ALL);
  }, [groups, gid]);

  // расход на текущую дату строится сразу при входе в режим — ровно один раз:
  // дальше он перестраивается только по «Показать» и по сбросу отбора, поэтому
  // условия отбора в зависимости не попадают
  useEffect(() => {
    if (date) dispatch(personnelActions.fetchPersonnel(date, facultyId, eyid, gid));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ------------------------------- отбор ------------------------------- */
  const handleDateChange = useCallback((value) => {
    setDate(value ? value.startOf('day') : null);
  }, []);

  // смена факультета пересобирает и курсы, и группы — нижестоящие поля сбрасываются
  const handleFacultyChange = useCallback((name, value) => {
    setFacultyId(value);
    setEyid(ALL);
    setGid(ALL);
  }, []);

  const handleCourseChange = useCallback((name, value) => {
    setEyid(value);
    setGid(ALL);
  }, []);

  const handleGroupChange = useCallback((name, value) => {
    setGid(value);
  }, []);

  const handleShow = useCallback(() => {
    if (!date) return;
    dispatch(personnelActions.fetchPersonnel(date, facultyId, eyid, gid));
  }, [date, facultyId, eyid, gid, dispatch]);

  const isDirty = facultyId !== ALL || eyid !== ALL || gid !== ALL
    || !date || !date.isSame(today(), 'day');

  const handleReset = useCallback(() => {
    const day = today();
    setDate(day);
    setFacultyId(ALL);
    setEyid(ALL);
    setGid(ALL);
    dispatch(personnelActions.fetchPersonnel(day, ALL, ALL, ALL));
  }, [dispatch]);

  /* ------------------------ перечень причин отсутствия ------------------------ */
  const closeMenu = useCallback(() => setMenu(null), []);

  const toggleMenu = useCallback((group, student, element) => {
    setMenu(prev => (prev && prev.gid === group.gid && prev.mid === student.mid
      ? null
      : { gid: group.gid, mid: student.mid, anchor: element.getBoundingClientRect() }));
  }, []);

  // перечень не должен выходить за границы окна: снизу не помещается — раскрывается вверх
  useLayoutEffect(() => {
    if (!menu) {
      setMenuPos(null);
      return;
    }
    const element = menuRef.current;
    if (!element) return;
    const { width, height } = element.getBoundingClientRect();
    const anchor = menu.anchor;
    const left = Math.max(12, Math.min(anchor.left, window.innerWidth - width - 12));
    let top = anchor.bottom + 4;
    if (top + height > window.innerHeight - 12) top = anchor.top - height - 4;
    top = Math.max(12, Math.min(top, window.innerHeight - height - 12));
    setMenuPos({ top, left });
  }, [menu]);

  // закрытие: Esc, клик мимо, прокрутка страницы, изменение размера окна
  useEffect(() => {
    if (!menu) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') closeMenu();
    };
    // клик по ячейке отметки закрывает перечень сам — иначе он успел бы закрыться
    // на mousedown и снова открыться по click
    const handleMouseDown = (event) => {
      const inside = menuRef.current && menuRef.current.contains(event.target);
      const onCell = event.target.closest && event.target.closest('[data-abs-cell]');
      if (!inside && !onCell) closeMenu();
    };

    // прокрутка самого перечня причин его не закрывает
    const handleScroll = (event) => {
      if (!menuRef.current || !menuRef.current.contains(event.target)) closeMenu();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', closeMenu);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', closeMenu);
    };
  }, [menu, closeMenu]);

  const flashSaved = useCallback((cell) => {
    if (savedTimer.current) clearTimeout(savedTimer.current);
    setSaved(cell);
    savedTimer.current = setTimeout(() => setSaved(null), SAVED_FLASH_MS);
  }, []);

  // выбор значения сразу уходит на сервер; в ячейке оно появляется после успешного ответа
  const handlePick = useCallback(async (student, option) => {
    const { gid: groupId, mid } = menu;
    const current = absenceOf(student);
    const nextValue = option ? option.value : '';
    closeMenu();

    if ((current ? current.value : '') === nextValue) return;

    let result;
    try {
      result = await dispatch(personnelActions.setGrade(mid, date, nextValue));
    } catch (e) {
      // недоступный сервер тоже должен объявляться, а не молча терять отметку
      result = { ok: false, error: 'Не удалось сохранить отметку: сервер недоступен' };
    }

    if (result && result.ok) {
      dispatch(personnelActions.setRashodAbsence(
        groupId,
        mid,
        option ? { value: option.value, text: option.text, valid: option.valid } : null
      ));
      flashSaved({ gid: groupId, mid });
      return;
    }

    // 403 объявляет общий обработчик запросов — второе сообщение о том же не нужно
    if (!result || result.status !== 403) {
      createNotification('error', (result && result.error) || 'Не удалось сохранить отметку');
    }
  }, [menu, date, dispatch, closeMenu, flashSaved]);

  /* ------------------------------- итоги ------------------------------- */
  const rashod = personnel || [];

  const totals = useMemo(() => rashod.reduce((acc, group) => {
    const students = group.students || [];
    return {
      groups: acc.groups + 1,
      students: acc.students + students.length,
      absent: acc.absent + students.filter(absenceOf).length,
    };
  }, { groups: 0, students: 0, absent: 0 }), [rashod]);

  /* ------------------------------- отрисовка ------------------------------- */
  const menuStudent = useMemo(() => {
    if (!menu) return null;
    const group = rashod.find(item => String(item.gid) === String(menu.gid));
    return (group && (group.students || []).find(item => String(item.mid) === String(menu.mid))) || null;
  }, [menu, rashod]);

  const renderAbsence = (group, student) => {
    const absence = absenceOf(student);
    const isOpen = !!menu && menu.gid === group.gid && menu.mid === student.mid;
    const isSaved = !!saved && saved.gid === group.gid && saved.mid === student.mid;

    const value = absence
      ? <span className={styles.absValue}>{absence.text}</span>
      : <span className={styles.absEmpty}>не отмечен</span>;

    return (
      <td className={cn(styles.absCell, styles.colAbs, isSaved && styles.saved)}>
        {canEdit ? (
          <button
            type='button'
            data-abs-cell='true'
            className={styles.absBtn}
            aria-haspopup='listbox'
            aria-expanded={isOpen}
            onClick={event => toggleMenu(group, student, event.currentTarget)}
          >
            {value}
            <IoIosArrowDown size={14} />
          </button>
        ) : (
          <span className={styles.absView}>{value}</span>
        )}
      </td>
    );
  };

  const renderGroup = (group) => {
    const students = group.students || [];
    const meta = groupMeta(group).join(' · ');

    return (
      <section className={styles.group} key={group.gid}>
        <div className={styles.groupHead}>
          <span className={styles.groupName}>{group.name}</span>
          {!!meta && <span className={styles.groupMeta}>{meta}</span>}
          <span className={styles.groupCount}>обучающихся: <b>{students.length}</b></span>
        </div>

        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.colNum}>№ п/п</th>
                <th className={styles.colRank}>Звание</th>
                <th>Фамилия И.О.</th>
                <th className={styles.colAbs}>Причина отсутствия</th>
              </tr>
            </thead>
            <tbody>
              {students.map((student, index) => (
                <tr key={student.mid} className={cn(absenceOf(student) && styles.gone)}>
                  <td className={cn(styles.colNum, styles.mono)}>{student.n ?? index + 1}</td>
                  <td className={styles.colRank}>
                    {student.rank || <span className={styles.dash}>—</span>}
                  </td>
                  <td className={styles.strong}>{student.fio}</td>
                  {renderAbsence(group, student)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    );
  };

  const renderList = () => {
    // расход ещё не строился — такое возможно только при не заданной дате
    if (!date) {
      return (
        <div className={styles.empty}>
          <div className={styles.emptyTitle}>Задайте условия и нажмите «Показать»</div>
          Расход строится на дату: укажите её в отборе.
        </div>
      );
    }

    if (!loaded) return null;

    if (!rashod.length) {
      return (
        <div className={styles.empty}>
          <div className={styles.emptyTitle}>По заданным условиям учебные группы не найдены</div>
          Проверьте дату и отборы: на выбранную дату в группе должен числиться хотя бы один обучающийся.
        </div>
      );
    }

    return rashod.map(renderGroup);
  };

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <div className={styles.headText}>
          <h1 className={styles.title}>Расход личного состава</h1>
          <p className={styles.sub}>
            Состав обучающихся по учебным группам на выбранную дату и отметки отсутствия
          </p>
        </div>

        <div className={styles.headRight}>
          {!!fromJournal && (
            <button type='button' className={styles.backLink} onClick={() => history.push('/journal')}>
              <IoIosArrowBack size={16} />
              Вернуться в журнал
            </button>
          )}
          <button
            type='button'
            className={styles.resetBtn}
            onClick={handleReset}
            disabled={!isDirty}
          >
            Сбросить отбор
            <FaRegTrashAlt size={16} />
          </button>
        </div>
      </div>

      <div className={styles.filters}>
        <div className={styles.fDate}>
          <label className={styles.fieldLabel} htmlFor='rashodDate'>Дата</label>
          <DatePicker
            id='rashodDate'
            selected={date}
            onChange={handleDateChange}
            dateFormat={DATE_FORMAT}
            locale='ru'
            todayButton='Сегодня'
            showYearDropdown
            placeholderText='ДД.ММ.ГГГГ'
            className={styles.dateField}
            popperClassName={styles.datePopper}
          />
        </div>

        <div className={styles.fFac}>
          <CustomSelect
            title='Факультет'
            width='100%'
            options={facultyOptions}
            value={facultyId}
            setFieldValue={handleFacultyChange}
            valuesName='faculty'
          />
        </div>

        <div className={styles.fCrs}>
          <CustomSelect
            title='Курс'
            width='100%'
            options={courseOptions}
            value={eyid}
            setFieldValue={handleCourseChange}
            valuesName='course'
          />
        </div>

        <div className={styles.fGrp}>
          <CustomSelect
            title='Группа'
            width='100%'
            options={groupOptions}
            value={gid}
            setFieldValue={handleGroupChange}
            valuesName='group'
          />
        </div>

        <button type='button' className={styles.refreshBtn} onClick={handleShow}>
          Показать
        </button>
      </div>

      <div className={cn(styles.listbox, loading && styles.listboxLoading)}>
        {renderList()}
        {loading && (
          <div className={styles.load}>
            <Spinner animation='border' role='status' className={styles.spinner}>
              <span className='sr-only'>Загрузка…</span>
            </Spinner>
          </div>
        )}
      </div>

      {!!rashod.length && (
        <div className={styles.footrow}>
          <span className={styles.total}>
            Групп: <b>{totals.groups}</b> · обучающихся: <b>{totals.students}</b>
            {' · '}отмечено отсутствий: <b>{totals.absent}</b>
          </span>
        </div>
      )}

      {!!menu && !!menuStudent && createPortal(
        <div
          ref={menuRef}
          className={styles.dropdown}
          role='listbox'
          style={{
            top: menuPos ? menuPos.top : -9999,
            left: menuPos ? menuPos.left : -9999,
            visibility: menuPos ? 'visible' : 'hidden',
          }}
        >
          {(absentGrades || []).map(option => {
            const on = (absenceOf(menuStudent) || {}).value === option.value;
            return (
              <button
                type='button'
                key={option.value}
                role='option'
                aria-selected={on}
                className={cn(on && styles.optionOn)}
                onClick={() => handlePick(menuStudent, option)}
              >
                {option.text}
              </button>
            );
          })}
          {!!absenceOf(menuStudent) && (
            <>
              <div className={styles.optionSep} />
              <button
                type='button'
                className={styles.optionClear}
                onClick={() => handlePick(menuStudent, null)}
              >
                снять отметку
              </button>
            </>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default Personnel;
